// === AANSPRAKELIJKHEID (AVP) — payload build/clean/link helpers ===
// Pure functions extracted from AansprakelijkheidScreen. The AVP payload is assembled as
// schema-defaults -> deepMerge -> applyProductDefaults -> linkRelationData, then cleaned to the
// request schema by cleanAvpForRequest. Impure inputs (now/appName/baseUrl) are injected via
// opts so the chain is deterministic + offline-testable (see calc.test.ts).

import { AVP_PRODUCT, GSS_CODE_TO_I18N_KEY } from './fields';

export { extractDefaults } from '../../../utils/extractDefaults';

// Behavior change (2026-07-03 review fix): the shared deepMerge has the Array.isArray guard the
// local copy lacked — arrays now survive the schema-defaults merge instead of corrupting to {"0": ...}.
export { deepMerge } from '../../../utils/deepMerge';

// Whitelist helper: a new object containing only the `keys` that are defined on `src`.
function pickDefined(src: any, keys: string[]): Record<string, any> {
  const out: Record<string, any> = {};
  if (!src) return out;
  for (const k of keys) {
    if (src[k] !== undefined) out[k] = src[k];
  }
  return out;
}

export function cleanAvpForRequest(raw: any, opts: { t: (key: string) => string; now: Date }): any {
  const { t, now } = opts;
  const cd = raw?.Contractdocument;
  if (!cd) return { Contractdocument: {} };

  const pp = cd.PP || {};

  // --- AL: only BY, AL_DATACAT, AL_APPLNM, AL_URLNAAM ---
  const rawAL = cd.AL || {};
  const cleanAL: any = {};
  if (rawAL.BY && Array.isArray(rawAL.BY)) {
    cleanAL.BY = rawAL.BY.map((item: any) => {
      const cleaned: any = {};
      if (item.BY_URLBY !== undefined) cleaned.BY_URLBY = item.BY_URLBY;
      if (item.BY_BYLOMS !== undefined) cleaned.BY_BYLOMS = item.BY_BYLOMS;
      if (item.BY_BYLSRT !== undefined) cleaned.BY_BYLSRT = String(item.BY_BYLSRT);
      return cleaned;
    });
  }
  if (rawAL.AL_DATACAT !== undefined) cleanAL.AL_DATACAT = rawAL.AL_DATACAT;
  if (rawAL.AL_APPLNM !== undefined) cleanAL.AL_APPLNM = rawAL.AL_APPLNM;
  if (rawAL.AL_URLNAAM !== undefined) cleanAL.AL_URLNAAM = rawAL.AL_URLNAAM;

  // --- RI: whitelist + ensure RI_ALGVRWD ---
  const riKeys = ['RI_EGD', 'RI_OGB', 'RI_POLJUS', 'RI_OVRMED', 'RI_CGGDA', 'RI_STVOOP', 'RI_ALGVRWD'];
  const rawRI = pp.RI || {};
  const cleanRI: any = {};
  for (const k of riKeys) {
    cleanRI[k] = rawRI[k] ?? 'N';
  }

  // --- VP: whitelist (strip VP_VRWRKCD) ---
  const vpKeys = ['VP_ANAAM', 'VP_VOORL', 'VP_VOORV', 'VP_STRAAT', 'VP_HUISNR', 'VP_TOEVOEG', 'VP_PLAATS', 'VP_PCODE', 'VP_GEBDAT', 'VP_PARZAKC'];
  const rawVP = pp.VP || {};
  const cleanVP = pickDefined(rawVP, vpKeys);

  // --- VZ: whitelist + ensure required fields ---
  const vzKeys = ['VZ_RELSRTC', 'VZ_VOORL', 'VZ_VOORV', 'VZ_GESLACH', 'VZ_GEBDAT', 'VZ_ANAAM'];
  const rawVZ = pp.VZ || {};
  const cleanVZ = pickDefined(rawVZ, vzKeys);
  // Fill required VZ fields from VP if missing
  if (!cleanVZ.VZ_RELSRTC) cleanVZ.VZ_RELSRTC = '1';
  if (!cleanVZ.VZ_VOORL && cleanVP.VP_VOORL) cleanVZ.VZ_VOORL = cleanVP.VP_VOORL;
  if (!cleanVZ.VZ_ANAAM && cleanVP.VP_ANAAM) cleanVZ.VZ_ANAAM = cleanVP.VP_ANAAM;
  if (cleanVZ.VZ_GEBDAT === undefined && cleanVP.VP_GEBDAT !== undefined) cleanVZ.VZ_GEBDAT = cleanVP.VP_GEBDAT;

  // --- DA: whitelist + ensure MP ---
  const daKeys = ['MP', 'DA_VOLGNUM', 'DA_CODE', 'DA_GSS', 'DA_VERZSOM', 'DA_ERB', 'DA_BTP', 'DA_PPRC', 'DA_GEBIED', 'DA_WPREMBP', 'DA_ENTREF', 'DA_NTP', 'DA_TASS', 'DA_TPP', 'DA_TTOT', 'DA_TTPPRV', 'DA_TTSLTB', 'DA_PTSLTB', 'DA_PKRTVMP', 'DA_TKRT', 'DA_TTSL', 'DA_TPKRTVM'];
  const rawDA = pp.DA || {};
  const cleanDA = pickDefined(rawDA, daKeys);
  if (!cleanDA.MP) cleanDA.MP = [];
  if (!cleanDA.DA_WPREMBP) cleanDA.DA_WPREMBP = '4';
  if (typeof cleanDA.DA_WPREMBP !== 'string') cleanDA.DA_WPREMBP = String(cleanDA.DA_WPREMBP);
  // Defensive: BFF rejects with -9999 "Verplicht veld ontbreekt" when these are missing.
  // applyProductDefaults sets them on mount, but a previous failed-calculate response that
  // got merged into `data` (or stale localStorage from an earlier session) can drop them.
  // Re-apply at request time so the call always has the fields the AVP backend requires.
  if (!cleanDA.DA_CODE) cleanDA.DA_CODE = '6001';
  if (!cleanDA.DA_ENTREF) cleanDA.DA_ENTREF = '6001';
  if (!cleanDA.DA_GEBIED) cleanDA.DA_GEBIED = 'W';
  if (cleanDA.DA_VERZSOM === undefined) cleanDA.DA_VERZSOM = 5000000;
  if (cleanDA.DA_PPRC === undefined) cleanDA.DA_PPRC = 20;

  // --- VL: ensure required VL_MYAAND ---
  const rawVL = pp.VL || {};
  const cleanVL: any = {};
  if (rawVL.VL_VOLGNUM !== undefined) cleanVL.VL_VOLGNUM = rawVL.VL_VOLGNUM;
  cleanVL.VL_MYAAND = rawVL.VL_MYAAND || pp.PP_MYAAND || AVP_PRODUCT.carrierCode;

  // --- VE: ensure required VE_MYAAND ---
  const rawVE = pp.VE || {};
  const cleanVE: any = {};
  if (rawVE.VE_VOLGNUM !== undefined) cleanVE.VE_VOLGNUM = rawVE.VE_VOLGNUM;
  cleanVE.VE_MYAAND = rawVE.VE_MYAAND || 'IP01';

  // --- CL: whitelist items ---
  const clKeys = ['CL_VOLGNUM', 'CL_CLAUSNR', 'CL_CLAUSOM', 'CL_CLAUTXT', 'CL_CLAUVRW', 'CL_VERSIEC', 'CL_VRWRKCD'];
  const cleanCL = (pp.CL || []).map((item: any) => pickDefined(item, clKeys));

  // --- VW: whitelist items ---
  const vwKeys = ['VW_VOLGNUM', 'VW_VRWNR', 'VW_VRWOM', 'VW_VRWVERS', 'VW_URL', 'VW_BRONVRW', 'VW_VRWRKCD'];
  const cleanVW = (pp.VW || []).map((item: any) => pickDefined(item, vwKeys));

  // --- PP scalar fields: whitelist ---
  const ppScalarKeys = [
    'PP_EXTERN', 'PP_BRANCHE', 'PP_MYAAND', 'PP_INGDAT', 'PP_CDUUMND',
    'PP_HVVDAT', 'PP_BETTERM', 'PP_INGDTW', 'PP_STATUS', 'PP_AFDDEFN',
    'PP_ADEFVRS', 'PP_SPECLIM', 'PP_HANDACC', 'PP_PRODNM', 'PP_PRODUCT', 'PP_CVS', 'PP_COLL',
    'PP_AGNTNR', 'PP_EKST', 'PP_PKST', 'PP_NTP', 'PP_BTP', 'PP_TASS',
    'PP_TPP', 'PP_TTOT', 'PP_TTPPRV', 'PP_TTSLTB', 'PP_PTSLTB',
    'PP_PKRTVMP', 'PP_TKRT', 'PP_TTSL', 'PP_AFWAFSP', 'PP_AFWAFST',
    'PP_SPLIMNR', 'PP_PVADAT', 'PP_EXPDAT', 'PP_MYNAAM', 'PP_WPREMBP',
    'PP_TPKRTVM', 'PP_TARDAT', 'PP_INCWIJZ', 'PP_IBAN', 'PP_SLOTAKK',
    'PP_STPRV', 'PP_PPRC', 'PP_CONTROM',
  ];
  const cleanPP: any = pickDefined(pp, ppScalarKeys);

  // Type coercions: PP_PVADAT and PP_EXPDAT must be strings
  if (cleanPP.PP_PVADAT !== undefined && typeof cleanPP.PP_PVADAT !== 'string') {
    cleanPP.PP_PVADAT = String(cleanPP.PP_PVADAT);
  }
  if (cleanPP.PP_EXPDAT !== undefined && typeof cleanPP.PP_EXPDAT !== 'string') {
    cleanPP.PP_EXPDAT = String(cleanPP.PP_EXPDAT);
  }

  // Ensure required PP fields
  if (cleanPP.PP_INCWIJZ === undefined) cleanPP.PP_INCWIJZ = 'I';
  if (cleanPP.PP_IBAN === undefined) cleanPP.PP_IBAN = '';
  if (cleanPP.PP_WPREMBP === undefined) cleanPP.PP_WPREMBP = '4';
  if (cleanPP.PP_PPRC === undefined) cleanPP.PP_PPRC = 20;
  // Defensive: same reasoning as the DA defaults above. applyProductDefaults sets these
  // on mount; a previous response merge or stale localStorage can drop them. Re-apply here.
  if (!cleanPP.PP_BETTERM) cleanPP.PP_BETTERM = 1;
  if (!cleanPP.PP_MYAAND) cleanPP.PP_MYAAND = AVP_PRODUCT.carrierCode;
  if (!cleanPP.PP_PRODNM) cleanPP.PP_PRODNM = 'rhion AVP2025';
  if (!cleanPP.PP_PRODUCT) cleanPP.PP_PRODUCT = cleanPP.PP_PRODNM;
  if (!cleanPP.PP_MYNAAM) cleanPP.PP_MYNAAM = 'Rhion';
  if (!cleanPP.PP_HANDACC) cleanPP.PP_HANDACC = 'N';
  if (cleanPP.PP_EKST === undefined) cleanPP.PP_EKST = 0;
  if (!cleanPP.PP_CDUUMND) cleanPP.PP_CDUUMND = 12;
  if (!cleanPP.PP_BRANCHE) cleanPP.PP_BRANCHE = '060';
  if (!cleanPP.PP_ADEFVRS) cleanPP.PP_ADEFVRS = '001.00';
  if (!cleanPP.PP_AFDDEFN) cleanPP.PP_AFDDEFN = '060-R515-rhion AVP2025';
  cleanPP.PP_STPRV = 'J';
  if (!cleanPP.PP_INGDAT || !cleanPP.PP_HVVDAT) {
    const today = now;
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    if (!cleanPP.PP_INGDAT) cleanPP.PP_INGDAT = Number(`${yyyy}${mm}${dd}`);
    if (!cleanPP.PP_HVVDAT) cleanPP.PP_HVVDAT = Number(`${yyyy + 1}${mm}01`);
  }
  // PP_CONTROM from DA_GSS — resolved via i18n (same mapping as WorkflowSummary/Confirmation)
  if (!cleanPP.PP_CONTROM && cleanDA.DA_GSS) {
    const gssKey = GSS_CODE_TO_I18N_KEY[cleanDA.DA_GSS];
    cleanPP.PP_CONTROM = gssKey ? t(gssKey) : '';
  }

  // Assemble PP with sub-objects
  cleanPP.RI = cleanRI;
  cleanPP.VP = cleanVP;
  cleanPP.VZ = cleanVZ;
  cleanPP.DA = cleanDA;
  cleanPP.VL = cleanVL;
  cleanPP.VE = cleanVE;
  cleanPP.CL = cleanCL;
  cleanPP.VW = cleanVW;
  cleanPP.TP = {};
  cleanPP.QS = { QA: pp.QS?.QA || [] };

  // XG from PP level (not Contractdocument level)
  if (pp.XG) {
    const cleanXG: any = {};
    if (pp.XG.XM) {
      const cleanXM: any = {};
      if (pp.XG.XM.XM_STATTXT !== undefined) cleanXM.XM_STATTXT = pp.XG.XM.XM_STATTXT;
      cleanXG.XM = cleanXM;
    }
    cleanPP.XG = cleanXG;
  }

  return {
    Contractdocument: {
      AL: cleanAL,
      PP: cleanPP,
    },
  };
}

export function applyProductDefaults(data: any, opts: { appName: string; baseUrl: string; now: Date }): any {
  // Get application name and base URL for AL fields
  const { appName, baseUrl, now } = opts;
  const result = { ...data };

  // Ensure Contractdocument.PP structure exists
  if (!result.Contractdocument) {
    result.Contractdocument = {};
  }
  if (!result.Contractdocument.PP) {
    result.Contractdocument.PP = {};
  }

  // === PP-level defaults ===

  // PP_BETTERM - Betaaltermijn in maanden
  if (!result.Contractdocument.PP.PP_BETTERM) {
    result.Contractdocument.PP.PP_BETTERM = 1;
  }

  // PP_INGDAT & PP_HVVDAT - Dates in N8 format
  if (!result.Contractdocument.PP.PP_INGDAT || !result.Contractdocument.PP.PP_HVVDAT) {
    const today = now;
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const dateInN8Format = Number(`${year}${month}${day}`);

    if (!result.Contractdocument.PP.PP_INGDAT) {
      result.Contractdocument.PP.PP_INGDAT = dateInN8Format;
    }
    if (!result.Contractdocument.PP.PP_HVVDAT) {
      // Hoofdvervaldatum = first day of the current month, next year
      const hvvYear = today.getFullYear() + 1;
      const hvvMonth = String(today.getMonth() + 1).padStart(2, '0');
      result.Contractdocument.PP.PP_HVVDAT = Number(`${hvvYear}${hvvMonth}01`);
    }
  }

  // PP_MYAAND - Carrier code (maatschappij aanduiding)
  if (!result.Contractdocument.PP.PP_MYAAND) {
    result.Contractdocument.PP.PP_MYAAND = AVP_PRODUCT.carrierCode;
  }

  // PP_PRODNM - Product name (productnaam maatschappij)
  if (!result.Contractdocument.PP.PP_PRODNM) {
    result.Contractdocument.PP.PP_PRODNM = AVP_PRODUCT.productName;
  }
  if (!result.Contractdocument.PP.PP_PRODUCT) {
    result.Contractdocument.PP.PP_PRODUCT = result.Contractdocument.PP.PP_PRODNM;
  }

  // PP_MYNAAM - Maatschappij naam (company name for Rhion)
  if (!result.Contractdocument.PP.PP_MYNAAM) {
    result.Contractdocument.PP.PP_MYNAAM = "Rhion";
  }

  // PP_HANDACC - Handmatige acceptatie
  if (!result.Contractdocument.PP.PP_HANDACC) {
    result.Contractdocument.PP.PP_HANDACC = 'N';
  }

  // PP_EKST - Servicevergoeding is 0 at product level; charged once at package level (PK_TINV)
  result.Contractdocument.PP.PP_EKST = 0;

  // PP_CDUUMND - Contractduur in maanden
  if (!result.Contractdocument.PP.PP_CDUUMND) {
    result.Contractdocument.PP.PP_CDUUMND = 12;
  }

  // PP_BRANCHE - ADN branchecode (060 for liability insurance)
  if (!result.Contractdocument.PP.PP_BRANCHE) {
    result.Contractdocument.PP.PP_BRANCHE = '060';
  }

  // PP_ADEFVRS - AFD definitieversienummer
  if (!result.Contractdocument.PP.PP_ADEFVRS) {
    result.Contractdocument.PP.PP_ADEFVRS = '001.00';
  }

  // PP_AFDDEFN - AFD definitienaam
  if (!result.Contractdocument.PP.PP_AFDDEFN) {
    result.Contractdocument.PP.PP_AFDDEFN = '060-R515-rhion AVP2025';
  }

  // === DA (Aansprakelijkheid Dekking) defaults ===

  if (!result.Contractdocument.PP.DA) {
    result.Contractdocument.PP.DA = {};
  }

  // DA_CODE - Dekkingscode (const)
  result.Contractdocument.PP.DA.DA_CODE = '6001';

  // DA_ENTREF - Entiteitsreferentie
  if (!result.Contractdocument.PP.DA.DA_ENTREF) {
    result.Contractdocument.PP.DA.DA_ENTREF = '6001';
  }

  // DA_GEBIED - Dekkingsgebied (always World)
  result.Contractdocument.PP.DA.DA_GEBIED = 'W';

  // DA_GSS - Gezinssamenstelling (required field - user must fill)
  // No default value - user must select

  // DA_VERZSOM - Verzekerde som (default 5000000, must be number per schema)
  if (!result.Contractdocument.PP.DA.DA_VERZSOM) {
    result.Contractdocument.PP.DA.DA_VERZSOM = 5000000;
  }

  result.Contractdocument.PP.DA.DA_PPRC = 20;

  // === VE (Verzekeraar) defaults ===

  if (!result.Contractdocument.PP.VE) {
    result.Contractdocument.PP.VE = {};
  }

  if (!result.Contractdocument.PP.VE.VE_MYAAND) {
    result.Contractdocument.PP.VE.VE_MYAAND = 'IP01';
  }

  // === VP defaults ===

  if (!result.Contractdocument.PP.VP) {
    result.Contractdocument.PP.VP = {};
  }

  // VP_PARZAKC - Particulier (P) or Zakelijk (default to Particulier)
  if (!result.Contractdocument.PP.VP.VP_PARZAKC) {
    result.Contractdocument.PP.VP.VP_PARZAKC = 'P';
  }

  // === RI (Risico-informatie) defaults - required by schema ===

  if (!result.Contractdocument.PP.RI) {
    result.Contractdocument.PP.RI = {};
  }
  if (!result.Contractdocument.PP.RI.RI_EGD) {
    result.Contractdocument.PP.RI.RI_EGD = 'N';
  }
  if (!result.Contractdocument.PP.RI.RI_OGB) {
    result.Contractdocument.PP.RI.RI_OGB = 'N';
  }
  if (!result.Contractdocument.PP.RI.RI_POLJUS) {
    result.Contractdocument.PP.RI.RI_POLJUS = 'N';
  }
  if (!result.Contractdocument.PP.RI.RI_OVRMED) {
    result.Contractdocument.PP.RI.RI_OVRMED = 'N';
  }
  if (!result.Contractdocument.PP.RI.RI_CGGDA) {
    result.Contractdocument.PP.RI.RI_CGGDA = 'N';
  }
  if (!result.Contractdocument.PP.RI.RI_STVOOP) {
    result.Contractdocument.PP.RI.RI_STVOOP = 'N';
  }
  if (!result.Contractdocument.PP.RI.RI_ALGVRWD) {
    result.Contractdocument.PP.RI.RI_ALGVRWD = 'N';
  }

  // === VZ (Verzekeringnemer) defaults - required by schema ===

  if (!result.Contractdocument.PP.VZ) {
    result.Contractdocument.PP.VZ = {};
  }
  if (!result.Contractdocument.PP.VZ.VZ_RELSRTC) {
    result.Contractdocument.PP.VZ.VZ_RELSRTC = '1';
  }

  // === VL defaults - required by schema ===

  if (!result.Contractdocument.PP.VL) {
    result.Contractdocument.PP.VL = {};
  }
  if (!result.Contractdocument.PP.VL.VL_MYAAND) {
    result.Contractdocument.PP.VL.VL_MYAAND = AVP_PRODUCT.carrierCode;
  }

  // PP_INCWIJZ - Incassowijze (default I = Incasso, automatisch)
  if (!result.Contractdocument.PP.PP_INCWIJZ) {
    result.Contractdocument.PP.PP_INCWIJZ = 'I';
  }

  // === TP, CL defaults ===

  if (!result.Contractdocument.PP.TP) {
    result.Contractdocument.PP.TP = {};
  }

  if (!result.Contractdocument.PP.CL) {
    result.Contractdocument.PP.CL = [];
  }

  // === MP array defaults (minItems: 0) ===

  if (!result.Contractdocument.PP.DA.MP) {
    result.Contractdocument.PP.DA.MP = [];
  }

  // === VW array defaults (kept empty for SAA AVP calculations) ===

  if (!result.Contractdocument.PP.VW) {
    result.Contractdocument.PP.VW = [];
  }

  // PP_IBAN - Bank account nummer (set in PaymentInformationScreen, initialize empty)
  if (!result.Contractdocument.PP.PP_IBAN) {
    result.Contractdocument.PP.PP_IBAN = '';
  }

  // === AL defaults ===

  if (!result.Contractdocument.AL) {
    result.Contractdocument.AL = {};
  }

  // Always ensure AL_APPLNM and AL_URLNAAM are set
  result.Contractdocument.AL = {
    AL_APPLNM: appName,
    AL_URLNAAM: baseUrl,
    ...result.Contractdocument.AL
  };


  return result;
}

export function linkRelationData(productData: any, relationData: any, opts: { now: Date }): any {
  if (!relationData || !relationData.clientInfo) {
    return productData;
  }

  const result = JSON.parse(JSON.stringify(productData));

  // Ensure Contractdocument.PP structure exists
  if (!result.Contractdocument) {
    result.Contractdocument = {};
  }
  if (!result.Contractdocument.PP) {
    result.Contractdocument.PP = {};
  }
  if (!result.Contractdocument.PP.VP) {
    result.Contractdocument.PP.VP = {};
  }
  if (!result.Contractdocument.PP.BA) {
    result.Contractdocument.PP.BA = {};
  }

  // Link client information fields to PP.VP
  if (relationData.clientInfo.initials) {
    result.Contractdocument.PP.VP.VP_VOORL = relationData.clientInfo.initials;
  }
  if (relationData.clientInfo.lastName) {
    result.Contractdocument.PP.VP.VP_ANAAM = relationData.clientInfo.lastName;
  }
  if (relationData.clientInfo.affix) {
    result.Contractdocument.PP.VP.VP_VOORV = relationData.clientInfo.affix;
  }

  // Link address fields from clientInfo to BA and VP
  // Only set if not already present in productData (preserve user-entered address from AVP)
  if (relationData.clientInfo.street && !result.Contractdocument.PP.BA.BA_STRAAT) {
    result.Contractdocument.PP.BA.BA_STRAAT = relationData.clientInfo.street;
    result.Contractdocument.PP.VP.VP_STRAAT = relationData.clientInfo.street;
  }
  if (relationData.clientInfo.houseNumber && !result.Contractdocument.PP.BA.BA_HUISNR) {
    const houseNr = Number(relationData.clientInfo.houseNumber);
    result.Contractdocument.PP.BA.BA_HUISNR = relationData.clientInfo.houseNumber;
    result.Contractdocument.PP.VP.VP_HUISNR = isNaN(houseNr) ? 0 : houseNr;
  }
  if (relationData.clientInfo.houseNumberAddition && !result.Contractdocument.PP.BA.BA_TOEVOEG) {
    result.Contractdocument.PP.BA.BA_TOEVOEG = relationData.clientInfo.houseNumberAddition;
  }
  if (relationData.clientInfo.postalCode && !result.Contractdocument.PP.BA.BA_PCODE) {
    result.Contractdocument.PP.BA.BA_PCODE = relationData.clientInfo.postalCode;
    result.Contractdocument.PP.VP.VP_PCODE = relationData.clientInfo.postalCode;
  }
  if (relationData.clientInfo.city && !result.Contractdocument.PP.BA.BA_PLAATS) {
    result.Contractdocument.PP.BA.BA_PLAATS = relationData.clientInfo.city;
    result.Contractdocument.PP.VP.VP_PLAATS = relationData.clientInfo.city;
  }

  // Handle DD-MM-YYYY format from client information
  if (relationData.clientInfo.dateOfBirth) {
    let dateStr = relationData.clientInfo.dateOfBirth.replace(/-/g, '');
    if (dateStr.length === 8) {
      // Convert DDMMYYYY to YYYYMMDD
      const day = dateStr.substring(0, 2);
      const month = dateStr.substring(2, 4);
      const year = dateStr.substring(4, 8);
      dateStr = year + month + day;
    }
    const dateNum = Number(dateStr);

    const year = Math.floor(dateNum / 10000);
    const currentYear = opts.now.getFullYear();
    const isValidYear = year >= 1900 && year <= currentYear;

    if (!isNaN(dateNum) && dateStr.length === 8 && isValidYear) {
      result.Contractdocument.PP.VP.VP_GEBDAT = dateNum;

      // VZ_GEBDAT - required by schema
      if (!result.Contractdocument.PP.VZ) {
        result.Contractdocument.PP.VZ = {};
      }
      if (!result.Contractdocument.PP.VZ.VZ_GEBDAT) {
        result.Contractdocument.PP.VZ.VZ_GEBDAT = dateNum;
      }
    }
  }

  // Populate VZ (Verzekeringnemer) from client info - required by schema
  if (!result.Contractdocument.PP.VZ) {
    result.Contractdocument.PP.VZ = {};
  }
  if (relationData.clientInfo.initials && !result.Contractdocument.PP.VZ.VZ_VOORL) {
    result.Contractdocument.PP.VZ.VZ_VOORL = relationData.clientInfo.initials;
  }
  if (relationData.clientInfo.lastName && !result.Contractdocument.PP.VZ.VZ_ANAAM) {
    result.Contractdocument.PP.VZ.VZ_ANAAM = relationData.clientInfo.lastName;
  }
  if (relationData.clientInfo.affix && !result.Contractdocument.PP.VZ.VZ_VOORV) {
    result.Contractdocument.PP.VZ.VZ_VOORV = relationData.clientInfo.affix;
  }
  if (relationData.clientInfo.gender && !result.Contractdocument.PP.VZ.VZ_GESLACH) {
    result.Contractdocument.PP.VZ.VZ_GESLACH = relationData.clientInfo.gender;
  }

  // Ensure VP address fields are synced from BA if already present
  const ba = result.Contractdocument.PP.BA;
  if (ba) {
    if (ba.BA_HUISNR && !result.Contractdocument.PP.VP.VP_HUISNR) {
      const houseNr = Number(ba.BA_HUISNR);
      result.Contractdocument.PP.VP.VP_HUISNR = isNaN(houseNr) ? 0 : houseNr;
    }
    if (ba.BA_STRAAT && !result.Contractdocument.PP.VP.VP_STRAAT) {
      result.Contractdocument.PP.VP.VP_STRAAT = ba.BA_STRAAT;
    }
    if (ba.BA_PCODE && !result.Contractdocument.PP.VP.VP_PCODE) {
      result.Contractdocument.PP.VP.VP_PCODE = ba.BA_PCODE;
    }
    if (ba.BA_PLAATS && !result.Contractdocument.PP.VP.VP_PLAATS) {
      result.Contractdocument.PP.VP.VP_PLAATS = ba.BA_PLAATS;
    }
  }

  return result;
}

export function avpCalcInputSignature(d: any): string {
  const pp = d?.Contractdocument?.PP;
  return JSON.stringify({
    BA_PCODE: pp?.BA?.BA_PCODE,
    BA_HUISNR: pp?.BA?.BA_HUISNR,
    BA_TOEVOEG: pp?.BA?.BA_TOEVOEG || '',
    DA_GSS: pp?.DA?.DA_GSS,
    VP_GEBDAT: pp?.VP?.VP_GEBDAT,
  });
}

export function stripResponseBa(bffResult: any): any {
  if (!bffResult?.output?.Contractdocument?.PP) return bffResult;
  const { BA: _stripBA, ...ppWithoutBA } = bffResult.output.Contractdocument.PP;
  return {
    ...bffResult,
    output: {
      ...bffResult.output,
      Contractdocument: { ...bffResult.output.Contractdocument, PP: ppWithoutBA },
    },
  };
}

// BFF returns HTTP 200 with a stripped Contractdocument + an XG.XM error list when the risk is
// rejected (e.g. -9999 "Verplicht veld ontbreekt"). Returns the rejection message, or null when OK.
export function detectAvpRejection(output: any): string | null {
  const xg = output?.Contractdocument?.XG;
  const xmList = Array.isArray(xg?.XM) ? xg.XM : (xg?.XM ? [xg.XM] : []);
  const xmText = xmList.map((m: any) => m?.XM_STATTXT).filter(Boolean).join(' ');
  if (xg?.XG_STATUS && xg.XG_STATUS !== '00' && xmText) {
    return xmText || xg?.XG_STATUST || 'Calculation failed';
  }
  return null;
}

// Merge a BFF response into the current data, preserving user-entered BA + DA_VERZSOM + DA_GSS
// (the BFF doesn't echo these back). Returns the merged document.
export function mergeAvpResult(responseData: any, currentData: any): any {
  const current = currentData || {};
  const merged = { ...current, ...responseData };
  if (current?.Contractdocument?.PP?.BA) {
    if (!merged.Contractdocument) merged.Contractdocument = {};
    if (!merged.Contractdocument.PP) merged.Contractdocument.PP = {};
    merged.Contractdocument.PP.BA = {
      ...merged.Contractdocument?.PP?.BA,
      ...current.Contractdocument.PP.BA,
    };
  }
  if (current?.Contractdocument?.PP?.DA?.DA_VERZSOM) {
    if (!merged.Contractdocument) merged.Contractdocument = {};
    if (!merged.Contractdocument.PP) merged.Contractdocument.PP = {};
    if (!merged.Contractdocument.PP.DA) merged.Contractdocument.PP.DA = {};
    merged.Contractdocument.PP.DA.DA_VERZSOM = current.Contractdocument.PP.DA.DA_VERZSOM;
  }
  if (current?.Contractdocument?.PP?.DA?.DA_GSS) {
    if (!merged.Contractdocument) merged.Contractdocument = {};
    if (!merged.Contractdocument.PP) merged.Contractdocument.PP = {};
    if (!merged.Contractdocument.PP.DA) merged.Contractdocument.PP.DA = {};
    merged.Contractdocument.PP.DA.DA_GSS = current.Contractdocument.PP.DA.DA_GSS;
  }
  return merged;
}

// Bake the AVP discount into the merged data's DA + PP premium fields. Full precision kept (downstream
// does provision-strip x tax -> floor, which needs 3.145 not 3.14); 4-decimal round strips IEEE-754
// float artefacts. `percentage` is a whole number (e.g. 15). Mutates + returns mergedData.
export function applyAvpDiscount(mergedData: any, percentage: number): any {
  const f = 1 - percentage / 100;
  const r = (v: any) => Math.round(Number(v) * f * 10000) / 10000;
  const premiumFields = ['_BTP', '_NTP', '_TPP', '_TASS', '_TTOT'];
  const pp = mergedData?.Contractdocument?.PP;
  if (pp) {
    if (pp.DA) {
      for (const suffix of premiumFields) {
        const daKey = `DA${suffix}`;
        if (pp.DA[daKey] !== undefined) pp.DA[daKey] = r(pp.DA[daKey]);
      }
      for (const suffix of premiumFields) {
        const daKey = `DA${suffix}`;
        const ppKey = `PP${suffix}`;
        if (pp.DA[daKey] !== undefined) pp[ppKey] = pp.DA[daKey];
      }
      if (pp.DA.DA_NTP !== undefined) pp.PP_NTPVPK = pp.DA.DA_NTP;
    } else {
      for (const suffix of premiumFields) {
        const ppKey = `PP${suffix}`;
        if (pp[ppKey] !== undefined) pp[ppKey] = r(pp[ppKey]);
      }
      if (pp.PP_NTPVPK !== undefined) pp.PP_NTPVPK = r(pp.PP_NTPVPK);
    }
  }
  return mergedData;
}
